import type { SchemaDemandedDocumentEntry } from '~/features/leadDetails/schemas/demandedDocuments.schema';

const MONTHS_PER_YEAR = 12;

/** A month encoded as a single sortable integer (year * 12 + zero-based month). */
export type MonthKey = number;

// Pending range start = first click of an incomplete range
export type SelectionChange = { anchor: MonthKey | null; months: MonthKey[] };

/** A contiguous span of selected months (a single month has `start === end`). */
export type MonthRange = { end: MonthKey; start: MonthKey };

export const toMonthKey = (year: number, month: number): MonthKey => year * MONTHS_PER_YEAR + month;

export const fromMonthKey = (key: MonthKey): { year: number; month: number } => ({
    month: ((key % MONTHS_PER_YEAR) + MONTHS_PER_YEAR) % MONTHS_PER_YEAR,
    year: Math.floor(key / MONTHS_PER_YEAR),
});

const sortedUnique = (keys: Iterable<MonthKey>): MonthKey[] => [...new Set(keys)].sort((a, b) => a - b);

/** The document name the backend expects: "<category label> <German month> <year>". */
export const buildDocumentName = (categoryLabel: string, monthNames: string[], key: MonthKey): string => {
    const { month, year } = fromMonthKey(key);
    return `${categoryLabel} ${monthNames[month]} ${year}`;
};

// Last two name tokens are "<month> <year>"; label prefix may contain spaces
export const selectedMonthsFromDocuments = (
    documents: SchemaDemandedDocumentEntry[],
    monthNames: string[],
): MonthKey[] => {
    const nameToMonth = new Map(monthNames.map((name, index) => [name, index]));
    const keys: MonthKey[] = [];
    documents.forEach(({ name }) => {
        const tokens = name.trim().split(/\s+/);
        const year = Number(tokens[tokens.length - 1]);
        const month = nameToMonth.get(tokens[tokens.length - 2]);
        if (Number.isInteger(year) && month !== undefined) keys.push(toMonthKey(year, month));
    });
    return sortedUnique(keys);
};

// Second click fills the range; clicking a selected month deselects; gaps between selections fill
export const applyMonthClick = (
    clicked: MonthKey,
    previousKeys: MonthKey[],
    anchor: MonthKey | null,
): SelectionChange => {
    const previous = new Set(previousKeys);

    if (anchor !== null) {
        const [from, to] = anchor <= clicked ? [anchor, clicked] : [clicked, anchor];
        const filled = new Set(previous);
        for (let key = from; key <= to; key++) filled.add(key);
        return { anchor: null, months: sortedUnique(filled) };
    }

    if (previous.has(clicked)) {
        return { anchor: null, months: sortedUnique([...previous].filter((key) => key !== clicked)) };
    }

    const fillsGap = previous.has(clicked - 1) && previous.has(clicked + 1);
    return { anchor: fillsGap ? null : clicked, months: sortedUnique([...previous, clicked]) };
};

/** Merge the selected months into consecutive spans for the overview (a lone month has start === end). */
export const mergeMonthsToRanges = (keys: MonthKey[]): MonthRange[] => {
    const ranges: MonthRange[] = [];
    sortedUnique(keys).forEach((key) => {
        const last = ranges[ranges.length - 1];
        if (last && key === last.end + 1) last.end = key;
        else ranges.push({ end: key, start: key });
    });
    return ranges;
};

/** Every month covered by a range, so a whole span can be removed at once. */
export const monthKeysInRange = ({ end, start }: MonthRange): MonthKey[] => {
    const keys: MonthKey[] = [];
    for (let key = start; key <= end; key++) keys.push(key);
    return keys;
};

/** Build the requested document entries for the selected months. */
export const buildDocuments = (
    keys: MonthKey[],
    categoryLabel: string,
    monthNames: string[],
): SchemaDemandedDocumentEntry[] =>
    sortedUnique(keys).map((key) => ({ name: buildDocumentName(categoryLabel, monthNames, key), requested: true }));
